/**
 * `<caos-toast-stack>` — where toasts accumulate. (Catalog: Overlays › ToastStack — where toasts accumulate.)
 *
 * A polite live region that stacks its `<caos-toast>` children with a consistent
 * gap. By default it flows in place (so it composes inside any layout and the
 * gallery can show it inline); set `float` to pin it to a screen corner chosen by
 * `position` (`bottom-right` default · `bottom-left` · `top-right` · `top-left`) for
 * real overlay use. `max` caps how many toasts stay mounted — older ones past the
 * cap are removed so a burst cannot grow without bound. Styling seam: `::part(region)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import type { CaosToast } from './toast.js';

const sheet = makeSheet(`
  :host { display: block; }

  .region {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
  }
  /* newest on top when pinned to a bottom corner reads bottom-up */
  :host([position^="bottom"]) .region { flex-direction: column-reverse; }

  /* floating: pin to a corner and stack above the page (menus/toasts may float) */
  :host([float]) {
    position: fixed;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). The highest of
       the scale: a confirmation must never be hidden behind a dropdown, overlay or tooltip. */
    z-index: var(--caos-layer-toast);
    pointer-events: none;                 /* the region is transparent to the page */
  }
  :host([float]) .region > ::slotted(*) { pointer-events: auto; }  /* toasts stay interactive */

  :host([float]) { inset: auto var(--caos-space-5) var(--caos-space-5) auto; } /* default bottom-right */
  :host([float][position="bottom-left"])  { inset: auto auto var(--caos-space-5) var(--caos-space-5); }
  :host([float][position="top-right"])    { inset: var(--caos-space-5) var(--caos-space-5) auto auto; }
  :host([float][position="top-left"])     { inset: var(--caos-space-5) auto auto var(--caos-space-5); }
`);

export class CaosToastStack extends CaosElement {
  static get observedAttributes(): string[] {
    return ['max'];
  }

  private region: HTMLDivElement;
  private slotEl: HTMLSlotElement;

  constructor() {
    super(sheet);
    this.region = document.createElement('div');
    this.region.className = 'region';
    this.region.setAttribute('part', 'region');
    this.region.setAttribute('role', 'region');
    this.region.setAttribute('aria-label', 'Notifications');
    this.region.setAttribute('aria-live', 'polite');
    this.slotEl = document.createElement('slot');
    this.slotEl.addEventListener('slotchange', () => this.enforceMax());
    this.region.append(this.slotEl);
    this.root.append(this.region);
  }

  connectedCallback(): void {
    this.enforceMax();
  }

  attributeChangedCallback(): void {
    this.enforceMax();
  }

  /** Convenience: mount a toast into the stack (append = arrives newest). */
  push(toast: CaosToast): void {
    this.append(toast);
  }

  private enforceMax(): void {
    const max = Number(this.getAttribute('max'));
    if (!Number.isFinite(max) || max <= 0) return;
    const toasts = this.slotEl.assignedElements();
    // Drop the OLDEST (front of the DOM order) beyond the cap.
    for (let i = 0; i < toasts.length - max; i++) toasts[i]?.remove();
  }
}

define('caos-toast-stack', CaosToastStack);

declare global {
  interface HTMLElementTagNameMap {
    'caos-toast-stack': CaosToastStack;
  }
}
